<template>
    <li v-if="has_rights" class="nav-item account-panel-item">
        <template v-if="entry.url !== undefined">
            <a v-if="entry.onClick" class="nav-link account-panel-link" :href="entry.url" @click.prevent="entry.onClick" :class="entry.class">
                <span class="account-panel-link-visual"><feather :type="iconType" /></span>
                <span class="account-panel-link-label">{{ entryTitle }}</span>
            </a>
            <a v-else :href="entry.url" class="nav-link account-panel-link" :target="entry.target" :class="entry.class">
                <span class="account-panel-link-visual"><feather :type="iconType" /></span>
                <span class="account-panel-link-label">{{ entryTitle }}</span>
            </a>
        </template>
        <router-link v-else :to="entry.to" :class="entry.class" class="nav-link account-panel-link">
            <span class="account-panel-link-visual"><feather :type="iconType" /></span>
            <span class="account-panel-link-label">{{ entryTitle }}</span>
            <span class="account-panel-link-meta">
                <span v-if="entry.to.name === 'app_updates.list' && hasUnreadAppUpdates" class="red-bubble"></span>
                <newBadge v-if="hasNewBadge" />
                <feather type="chevron-right" class="account-panel-link-icon" />
            </span>
        </router-link>
    </li>
</template>

<script>
import newBadge from "../../components/NewBadges/newBadge";

export default {
    props: {
        entry: {
            required: true,
        },
        user: {
            default: null,
        },
    },
    computed: {
        hasUnreadAppUpdates() {
            return this.$store.getters["users/hasUnreadAppUpdates"];
        },
        entryTitle() {
            if ("undefined" !== typeof this.entry.titleI18n) return this.$t(`labels.routes.${this.entry.titleI18n}`);
            return this.entry.title;
        },
        hasNewBadge() {
            return this.entry && this.entry.to && this.entry.to.name ? this.checkForExistingNewBadge(this.entry.to.name) : false;
        },
        iconType() {
            const routeName = this.entry && this.entry.to ? this.entry.to.name : null;
            const routeIcons = {
                home: "grid",
                account: "user",
                restaurants: "map-pin",
                subscription: "credit-card",
                rgpd: "shield",
                employees: "users",
                sms: "message-square",
                online_help: "life-buoy",
                "app_updates.list": "bell",
                "restaurants.reserve_with_google": "search",
            };

            if (routeName && routeIcons[routeName]) {
                return routeIcons[routeName];
            }

            if (this.entry && this.entry.titleI18n === "documentation") {
                return "book-open";
            }

            if (this.entry && this.entry.titleI18n === "help") {
                return "help-circle";
            }

            return "circle";
        },
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        has_rights() {
            if (this.entry.rights.includes("any")) return true;
            return this.entry.rights.some((right) => {
                if (this.rights.includes(right)) return true;
                let i = right.indexOf("*");
                if (i !== -1) {
                    right = right.substring(0, i).replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
                    let regexp = new RegExp(`${right}.+`, "g");
                    return this.rights.some((cright) => cright.match(regexp));
                }
                return false;
            });
        },
    },
    components: {
        newBadge,
    },
};
</script>

<style lang="scss" scoped>
.account-panel-item {
    margin-bottom: 6px;
}

.account-panel-link {
    position: relative;
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    min-height: 42px;
    max-width: 100%;
    padding: 6px 10px 6px 7px;
    overflow: hidden;
    color: var(--account-panel-link-color, rgba(255, 255, 255, 0.78)) !important;
    font-size: 13px;
    font-weight: 750;
    line-height: 1.2;
    background: var(--account-panel-link-bg, rgba(255, 255, 255, 0.055));
    border: 1px solid var(--account-panel-link-border, rgba(255, 255, 255, 0.08));
    border-radius: 12px;
    box-shadow: var(--account-panel-link-shadow, inset 0 1px 0 rgba(255, 255, 255, 0.05));
    transition: color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.account-panel-link::before {
    position: absolute;
    inset: 0;
    pointer-events: none;
    content: "";
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.16), transparent 48%);
    opacity: 0;
    transition: opacity 0.18s ease;
}

.account-panel-link:hover,
.account-panel-link.router-link-exact-active {
    color: var(--account-panel-link-hover-color, #ffffff) !important;
    background: var(--account-panel-link-hover-bg, rgba(255, 255, 255, 0.12));
    border-color: var(--account-panel-link-hover-border, rgba(130, 202, 240, 0.32));
    box-shadow: var(
        --account-panel-link-hover-shadow,
        0 10px 24px rgba(0, 0, 0, 0.16),
        0 0 0 1px rgba(130, 202, 240, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.12)
    );
    transform: translateX(-2px);
}

.account-panel-link.router-link-exact-active {
    background: var(--account-panel-link-active-bg, linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(69, 156, 202, 0.16)));
}

.account-panel-link:hover::before,
.account-panel-link.router-link-exact-active::before {
    opacity: 1;
}

.account-panel-link-visual {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    color: var(--account-panel-link-visual-color, rgba(255, 255, 255, 0.76));
    background: var(--account-panel-link-visual-bg, rgba(255, 255, 255, 0.075));
    border: 1px solid var(--account-panel-link-border, rgba(255, 255, 255, 0.08));
    border-radius: 10px;
    transition: color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.account-panel-link-visual .feather {
    width: 15px;
    height: 15px;
    stroke-width: 2;
}

.account-panel-link:hover .account-panel-link-visual,
.account-panel-link.router-link-exact-active .account-panel-link-visual {
    color: var(--account-panel-link-hover-color, #ffffff);
    background: var(--account-panel-link-visual-hover-bg, rgba(130, 202, 240, 0.18));
    border-color: var(--account-panel-link-visual-hover-border, rgba(130, 202, 240, 0.24));
    box-shadow: var(--account-panel-link-visual-hover-shadow, 0 0 18px rgba(92, 181, 229, 0.2));
}

.account-panel-link-label {
    position: relative;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.account-panel-link-meta {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.account-panel-link-icon {
    width: 14px;
    height: 14px;
    color: var(--account-panel-link-icon-color, rgba(255, 255, 255, 0.42));
    transition: color 0.18s ease, transform 0.18s ease;
}

.account-panel-link:hover .account-panel-link-icon,
.account-panel-link.router-link-exact-active .account-panel-link-icon {
    color: var(--account-panel-link-icon-hover-color, rgba(255, 255, 255, 0.82));
    transform: translateX(2px);
}
</style>
